/**
 * Resource loader utility for campaigns and tournaments
 * Handles background image selection based on type, orientation, and collapsed state
 */
import type { CampaignTypes, ResourceType } from '../types/campaign.types';
import { MULTI_VIEW_MODE, SINGLE_VIEW_MODE } from '../../../constants';
import { normalizeCampaignAssets } from './campaignAssetsMapper';


export interface ResourceLoaderOptions {
  resources: ResourceType;
  isPortrait: boolean;
  isCollapsed: boolean;
  campaignType: CampaignTypes;
  viewMode: typeof SINGLE_VIEW_MODE | typeof MULTI_VIEW_MODE;
  hasMany: boolean;
  isPanel: boolean;
};

export interface BackgroundImageResult {
  backgroundImage: string;
  backgroundOverlayImage: string;
  backgroundOverlayLogo?: string;
};

export function getBackgroundImageUrl(options: ResourceLoaderOptions): BackgroundImageResult {
  const { resources, isPortrait, isCollapsed, campaignType, viewMode } = options;
  const isDesktop = window.innerWidth > 767;
  
  const normalizedAssets = normalizeCampaignAssets(resources, campaignType);
  
  let backgroundImage = '';
  let backgroundOverlayImage = '';
  let backgroundOverlayLogo = '';

  if (viewMode === MULTI_VIEW_MODE) {
    if (options.isPanel) {
      backgroundImage = isPortrait
        ? normalizedAssets.multiView.backgroundPortrait
        : normalizedAssets.multiView.backgroundLandscape;

      backgroundOverlayImage = isPortrait
        ? normalizedAssets.multiView.transparentOverlay
        : normalizedAssets.multiView.transparentOverlayDesktop;

      // Used for tournaments
      if(normalizedAssets.multiView.transparentOverlayLogo) {
        backgroundOverlayLogo = normalizedAssets.multiView.transparentOverlayLogo;
      }

      return { backgroundImage, backgroundOverlayImage, backgroundOverlayLogo };
    }
    
    if (options.hasMany) {
      backgroundImage = normalizedAssets.multiView.plate;
      return { backgroundImage, backgroundOverlayImage };
    }
  }

  if (viewMode === SINGLE_VIEW_MODE) {
    if (isCollapsed) {
      if (options.hasMany) {
        backgroundImage = normalizedAssets.singleView.plateClose;
        return { backgroundImage, backgroundOverlayImage };
      }

      backgroundImage = normalizedAssets.singleView.backgroundClose;
      return { backgroundImage, backgroundOverlayImage };
    }

    if (options.hasMany) {
      backgroundImage = normalizedAssets.singleView.plate;
      return { backgroundImage, backgroundOverlayImage };
    }

    backgroundImage = isPortrait && !isDesktop
      ? normalizedAssets.singleView.backgroundPortrait
      : normalizedAssets.singleView.backgroundDesktop;
    
    return { backgroundImage, backgroundOverlayImage };
  }

  return { backgroundImage, backgroundOverlayImage };
};

export function getBackgroundImageUrlWithType(
  resources: ResourceType,
  isPortrait: boolean,
  isCollapsed: boolean,
  campaignType: CampaignTypes,
  viewMode: typeof SINGLE_VIEW_MODE | typeof MULTI_VIEW_MODE,
  hasMany = false,
): BackgroundImageResult {
  return getBackgroundImageUrl({
    resources,
    isPortrait,
    isCollapsed,
    campaignType,
    viewMode,
    hasMany,
    isPanel: false,
  });
};